import { useNavigate } from 'react-router-dom';
import { useInfiniteQuery } from '@tanstack/react-query';
import classImg01 from '../../../assets/class_img01.png';
import classImg02 from '../../../assets/class_img02.png';
import classImg03 from '../../../assets/class_img03.png';
import classImg04 from '../../../assets/class_img04.png';
import classImg05 from '../../../assets/class_img05.png';
import classImg06 from '../../../assets/class_img06.png';

const ClassesSection = () => {
  const navigate = useNavigate();
  const API_URL = import.meta.env.VITE_API_URL;
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ['classList'],
      queryFn: async ({ pageParam = 1 }) => {
        const res = await fetch(
          `${API_URL}/bbs/ajax.get_class_list.php?page=${pageParam}`
        );
        return res.json();
      },
      getNextPageParam: (lastPage) => {
        const currentPage = lastPage.data.pagination.page;
        const totalPage = lastPage.data.pagination.total_page;
        return currentPage < totalPage ? currentPage + 1 : undefined;
      }
    });
  const imageList = {
    AI동화체험: classImg01,
    AI음악체험: classImg02,
    숫자나라: classImg03,
    호기심나라: classImg04,
    글쓰기나라: classImg05,
    그림나라: classImg06
  };
  const items = (data?.pages.flatMap((page) => page.data.list) ?? []).map(
    (item) => ({ ...item, file: imageList[item.name] || null })
  );

  return (
    <section id="classes" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6">
            인기 <span className="text-purple-600">AI 수업</span>
          </h2>
          <p className="md:text-xl text-lg text-gray-600 max-w-3xl mx-auto">
            아이들이 가장 좋아하는 AI 라이브 수업들을 만나보세요
          </p>
        </div>
        <div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          data-product-shop
        >
          {items.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <div className="relative w-full h-48 bg-gray-200 flex items-center justify-center">
                <span className="text-2xl font-bold text-gray-500">
                  Coming Soon
                </span>
              </div>
              <div className="p-6">
                <div className="flex flex-wrap gap-2 mb-3">
                  <span className="bg-gray-100 px-2 py-1 rounded-full text-xs">
                    준비중
                  </span>
                </div>
                <h3 className="text-xl font-bold text-gray-400 mb-3">
                  새로운 클래스 준비중입니다
                </h3>
                <p className="text-gray-400 mb-4">
                  곧 멋진 클래스가 추가될 예정입니다.
                </p>
                <div className="flex items-center justify-between">
                  <div className="text-gray-400">-</div>
                  <button
                    disabled
                    className="bg-gray-300 text-white px-6 py-2 rounded-full cursor-not-allowed"
                  >
                    Coming Soon
                  </button>
                </div>
              </div>
            </div>
          ) : (
            items.map((item) => (
              <div
                key={item.id}
                className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden"
              >
                <div className="relative">
                  {item.file ? (
                    <img
                      src={item.file}
                      alt={item.name}
                      className="w-full h-48 object-cover"
                    />
                  ) : (
                    <p className="text-2xl font-bold text-gray-500 h-48 flex items-center justify-center bg-gray-200">
                      No Image
                    </p>
                  )}
                  <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full">
                    <span className="text-sm font-bold text-purple-600">
                      {item.age}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex flex-wrap gap-2 mb-3">
                    {item.categories?.map((category, index) => (
                      <span
                        key={index}
                        className="bg-blue-100 text-blue-600 px-2 py-1 rounded-full text-xs font-medium"
                      >
                        {category}
                      </span>
                    ))}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 h-[60px] line-clamp-2">
                    <span>{item.course}</span>
                    <span>[강사 : {item.teachers.join(', ')}]</span>
                  </h3>
                  <p className="text-gray-600 leading-relaxed h-[25px] truncate">
                    {item.name}
                  </p>
                  <p className="text-gray-600 mb-4 leading-relaxed h-[25px] truncate">
                    {item.description}
                  </p>
                  <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-1">
                        <i className="ri-time-line"></i>
                        <span>{item.time}분</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <i className="ri-group-line"></i>
                        <span>{item.capacity}명</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="text-xl font-bold text-purple-600">
                      1회 수업료 {item.salePrice.toLocaleString()}원
                    </div>
                    <button
                      onClick={() => navigate(`/class/${item.id}`)}
                      className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-2 rounded-full hover:from-blue-600 hover:to-purple-700 transition-all whitespace-nowrap cursor-pointer"
                    >
                      클래스 보기
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
        <div className="text-center mt-16">
          {hasNextPage && (
            <button
              onClick={() => fetchNextPage()}
              className="bg-gradient-to-r from-purple-500 to-pink-600 text-white px-8 py-4 rounded-full text-lg font-bold hover:from-purple-600 hover:to-pink-700 transition-all transform hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-right-line mr-2"></i>
              수업 더 보기
            </button>
          )}
        </div>
      </div>
    </section>
  );
};

export default ClassesSection;
